import { create } from 'zustand';
import type {
  CampaignMap,
  CampaignPin,
  FogRegion,
  PartyLocation,
  PinState,
  CreatePinInput,
  UpdatePinInput,
} from '@machina/core';
import { trpc } from '../lib/trpc';
import { createSubscription } from '../lib/realtimeHelpers';

/**
 * Campaign Map Store
 *
 * Manages campaign/world map state including:
 * - Campaign map image
 * - Location pins
 * - Fog of war regions
 * - Party location tracking
 *
 * Separate from mapStore which handles battle maps at encounter scale.
 */

// Map view mode - campaign map vs battle map
export type MapMode = 'campaign' | 'battle' | 'iso';

// =============================================================================
// tRPC returns Dates as strings (JSON serialization)
// These helpers convert them back to proper types
// =============================================================================

type WithStringDates<T> = {
  [K in keyof T]: T[K] extends Date ? string : T[K];
};

function parseCampaignMap(raw: WithStringDates<CampaignMap>): CampaignMap {
  return {
    ...raw,
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
  };
}

function parseCampaignPin(raw: WithStringDates<CampaignPin>): CampaignPin {
  return {
    ...raw,
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
  };
}

function parseFogRegion(raw: WithStringDates<FogRegion>): FogRegion {
  return {
    ...raw,
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
  };
}

function parsePartyLocation(raw: WithStringDates<PartyLocation>): PartyLocation {
  return {
    ...raw,
    updatedAt: new Date(raw.updatedAt),
  };
}

interface CampaignMapState {
  // View Mode
  mapMode: MapMode;
  /** Remembers whether the GM's last battle view was top-down or iso, so
   *  the "Battle Map" button restores the last choice instead of always
   *  forcing top-down. Persisted to localStorage. */
  lastBattleStyle: 'battle' | 'iso';

  // Campaign Map Data
  activeCampaignMap: CampaignMap | null;
  pins: Map<string, CampaignPin>;
  regions: Map<string, FogRegion>;
  partyLocation: PartyLocation | null;

  // UI State
  selectedPinId: string | null;
  selectedRegionId: string | null;
  isEditingPins: boolean; // GM is in "place pin" mode
  isEditingRegions: boolean; // GM is adjusting region boundaries

  // Loading State
  isLoading: boolean;
  error: string | null;

  // Actions - View Mode
  setMapMode: (mode: MapMode) => void;

  // Actions - Campaign Map
  loadCampaignMap: (campaignId: string) => Promise<void>;
  setCampaignMap: (map: CampaignMap | null) => void;
  uploadCampaignMapImage: (
    campaignId: string,
    imageUrl: string,
    name: string,
    width: number,
    height: number
  ) => Promise<CampaignMap | null>;

  // Actions - Pins
  addPin: (input: CreatePinInput) => Promise<CampaignPin | null>;
  updatePin: (input: UpdatePinInput) => Promise<void>;
  deletePin: (pinId: string) => Promise<void>;
  setPinState: (pinId: string, state: PinState) => Promise<void>;
  selectPin: (pinId: string | null) => void;
  setEditingPins: (editing: boolean) => void;

  // Actions - Regions
  setRegions: (regions: FogRegion[]) => void;
  generateRegions: () => Promise<void>;
  revealRegion: (regionId: string) => Promise<void>;
  hideRegion: (regionId: string) => Promise<void>;
  selectRegion: (regionId: string | null) => void;
  setEditingRegions: (editing: boolean) => void;

  // Actions - Party Location
  setPartyLocation: (pinId: string | null, x?: number, y?: number) => Promise<void>;

  // Actions - Navigation
  travelToPin: (pinId: string) => Promise<{ sceneId: string } | null>;

  // Queries
  getVisiblePins: (isGM: boolean) => CampaignPin[];
  getRevealedRegions: () => FogRegion[];
  getPinsByRegion: (regionId: string) => CampaignPin[];
  getPinById: (pinId: string) => CampaignPin | undefined;
  getRegionById: (regionId: string) => FogRegion | undefined;

  // Change counter — bumped whenever pin states change, for reliable React dependency tracking
  pinVersion: number;

  // Realtime — pin state changes push to all clients
  realtimeCleanup: (() => void) | null;
  subscribeToPinRealtime: (campaignMapId: string) => void;
  unsubscribeFromPinRealtime: () => void;
}

// Restore last battle style preference from localStorage — default to top-down.
function getSavedBattleStyle(): 'battle' | 'iso' {
  try {
    const raw = localStorage.getItem('machina:last-battle-style');
    return raw === 'iso' ? 'iso' : 'battle';
  } catch { return 'battle'; }
}

export const useCampaignMapStore = create<CampaignMapState>((set, get) => ({
  // Initial State
  mapMode: 'battle', // LOCKED to battle mode for stability
  lastBattleStyle: getSavedBattleStyle(),
  activeCampaignMap: null,
  pins: new Map(),
  regions: new Map(),
  partyLocation: null,
  selectedPinId: null,
  selectedRegionId: null,
  isEditingPins: false,
  isEditingRegions: false,
  isLoading: false,
  error: null,
  pinVersion: 0,
  realtimeCleanup: null,

  // ==========================================================================
  // VIEW MODE
  // ==========================================================================

  setMapMode: (mode) => {
    // Local-only toggle — no Supabase sync (was causing view jumping loops)
    // Each player controls their own view independently
    const updates: Partial<CampaignMapState> = { mapMode: mode };
    // Remember the battle style so Battle Map button restores it next time
    if (mode === 'battle' || mode === 'iso') {
      updates.lastBattleStyle = mode;
      try { localStorage.setItem('machina:last-battle-style', mode); } catch { /* quota */ }
    }
    set(updates);
  },

  // ==========================================================================
  // CAMPAIGN MAP
  // ==========================================================================

  loadCampaignMap: async (campaignId) => {
    set({ isLoading: true, error: null });

    try {
      console.log('[CampaignMapStore] Loading campaign map for:', campaignId);

      const result = await trpc.campaignMaps.get.query({ campaignId });

      if (result.campaignMap) {
        // Convert pins and regions arrays to Maps, parsing dates
        const pinsMap = new Map<string, CampaignPin>();
        for (const pin of result.pins || []) {
          pinsMap.set(pin.id, parseCampaignPin(pin as WithStringDates<CampaignPin>));
        }

        const regionsMap = new Map<string, FogRegion>();
        for (const region of result.regions || []) {
          regionsMap.set(region.id, parseFogRegion(region as WithStringDates<FogRegion>));
        }

        const campaignMap = parseCampaignMap(result.campaignMap as WithStringDates<CampaignMap>);
        const partyLocation = result.partyLocation
          ? parsePartyLocation(result.partyLocation as WithStringDates<PartyLocation>)
          : null;

        set({
          activeCampaignMap: campaignMap,
          pins: pinsMap,
          regions: regionsMap,
          partyLocation,
          isLoading: false,
          // Don't auto-switch map mode - let user control their view
        });

        console.log('[CampaignMapStore] Loaded campaign map:', campaignMap.name);

        // Subscribe to realtime pin updates
        get().subscribeToPinRealtime(campaignMap.id);
      } else {
        set({
          activeCampaignMap: null,
          pins: new Map(),
          regions: new Map(),
          partyLocation: null,
          isLoading: false,
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load campaign map';
      console.error('[CampaignMapStore] Load error:', error);
      set({ isLoading: false, error: message });
    }
  },

  setCampaignMap: (map) => {
    set({ activeCampaignMap: map });
  },

  uploadCampaignMapImage: async (campaignId, imageUrl, name, width, height) => {
    set({ isLoading: true, error: null });

    try {
      const result = await trpc.campaignMaps.create.mutate({
        campaignId,
        name,
        imageUrl,
        imageWidth: width,
        imageHeight: height,
      });

      if (result.campaignMap) {
        const campaignMap = parseCampaignMap(result.campaignMap as WithStringDates<CampaignMap>);
        set({
          activeCampaignMap: campaignMap,
          pins: new Map(),
          regions: new Map(),
          partyLocation: null,
          isLoading: false,
        });
        return campaignMap;
      }

      set({ isLoading: false });
      return null;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create campaign map';
      console.error('[CampaignMapStore] Create error:', error);
      set({ isLoading: false, error: message });
      return null;
    }
  },

  // ==========================================================================
  // PINS
  // ==========================================================================

  addPin: async (input) => {
    try {
      const result = await trpc.campaignMaps.createPin.mutate(input);

      if (result.pin) {
        const pin = parseCampaignPin(result.pin as WithStringDates<CampaignPin>);
        set((state) => {
          const newPins = new Map(state.pins);
          newPins.set(pin.id, pin);
          return { pins: newPins, pinVersion: state.pinVersion + 1 };
        });
        return pin;
      }
      return null;
    } catch (error) {
      console.error('[CampaignMapStore] Add pin error:', error);
      return null;
    }
  },

  updatePin: async (input) => {
    try {
      await trpc.campaignMaps.updatePin.mutate(input);

      // Optimistic update + bump pinVersion so the MapCanvas effect that
      // listens on [pinVersion, pins] repaints the fog. Without the bump
      // a Map mutation alone sometimes doesn't trigger React's identity
      // check on the dep, so fog wouldn't refresh after a pin move or
      // rename.
      set((state) => {
        const pin = state.pins.get(input.pinId);
        if (!pin) return state;

        const newPins = new Map(state.pins);
        newPins.set(input.pinId, {
          ...pin,
          ...input,
          updatedAt: new Date(),
        } as CampaignPin);
        return { pins: newPins, pinVersion: state.pinVersion + 1 };
      });
    } catch (error) {
      console.error('[CampaignMapStore] Update pin error:', error);
    }
  },

  deletePin: async (pinId) => {
    try {
      await trpc.campaignMaps.deletePin.mutate({ pinId });

      set((state) => {
        const newPins = new Map(state.pins);
        newPins.delete(pinId);
        return {
          pins: newPins,
          pinVersion: state.pinVersion + 1,
          selectedPinId: state.selectedPinId === pinId ? null : state.selectedPinId,
        };
      });
    } catch (error) {
      console.error('[CampaignMapStore] Delete pin error:', error);
    }
  },

  setPinState: async (pinId, state) => {
    try {
      await trpc.campaignMaps.updatePin.mutate({ pinId, state });

      set((currentState) => {
        const pin = currentState.pins.get(pinId);
        if (!pin) return currentState;

        const newPins = new Map(currentState.pins);
        newPins.set(pinId, { ...pin, state, updatedAt: new Date() });
        return { pins: newPins, pinVersion: currentState.pinVersion + 1 };
      });
    } catch (error) {
      console.error('[CampaignMapStore] Set pin state error:', error);
    }
  },

  selectPin: (pinId) => {
    set({ selectedPinId: pinId });
  },

  setEditingPins: (editing) => {
    set({ isEditingPins: editing });
  },

  // ==========================================================================
  // REGIONS
  // ==========================================================================

  setRegions: (regions) => {
    const regionsMap = new Map<string, FogRegion>();
    for (const region of regions) {
      regionsMap.set(region.id, region);
    }
    set({ regions: regionsMap });
  },

  generateRegions: async () => {
    const { activeCampaignMap } = get();
    if (!activeCampaignMap) return;

    set({ isLoading: true, error: null });

    try {
      const result = await trpc.campaignMaps.generateRegions.mutate({
        campaignMapId: activeCampaignMap.id,
      });

      if (result.regions) {
        const regionsMap = new Map<string, FogRegion>();
        for (const region of result.regions) {
          regionsMap.set(region.id, parseFogRegion(region as WithStringDates<FogRegion>));
        }
        set({ regions: regionsMap, isLoading: false });

        // Update pin region assignments if returned
        if (result.updatedPins) {
          set((state) => {
            const newPins = new Map(state.pins);
            for (const updatedPin of result.updatedPins) {
              const existing = newPins.get(updatedPin.id);
              if (existing) {
                newPins.set(updatedPin.id, { ...existing, regionId: updatedPin.regionId ?? undefined });
              }
            }
            return { pins: newPins };
          });
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to generate regions';
      console.error('[CampaignMapStore] Generate regions error:', error);
      set({ isLoading: false, error: message });
    }
  },

  revealRegion: async (regionId) => {
    try {
      await trpc.campaignMaps.revealRegion.mutate({ regionId, reveal: true });

      set((state) => {
        const region = state.regions.get(regionId);
        if (!region) return state;

        const newRegions = new Map(state.regions);
        newRegions.set(regionId, { ...region, isRevealed: true, updatedAt: new Date() });
        return { regions: newRegions };
      });
    } catch (error) {
      console.error('[CampaignMapStore] Reveal region error:', error);
    }
  },

  hideRegion: async (regionId) => {
    try {
      await trpc.campaignMaps.revealRegion.mutate({ regionId, reveal: false });

      set((state) => {
        const region = state.regions.get(regionId);
        if (!region) return state;

        const newRegions = new Map(state.regions);
        newRegions.set(regionId, { ...region, isRevealed: false, updatedAt: new Date() });
        return { regions: newRegions };
      });
    } catch (error) {
      console.error('[CampaignMapStore] Hide region error:', error);
    }
  },

  selectRegion: (regionId) => {
    set({ selectedRegionId: regionId });
  },

  setEditingRegions: (editing) => {
    set({ isEditingRegions: editing });
  },

  // ==========================================================================
  // PARTY LOCATION
  // ==========================================================================

  setPartyLocation: async (pinId, x, y) => {
    const { activeCampaignMap } = get();
    if (!activeCampaignMap) return;

    try {
      await trpc.campaignMaps.setPartyLocation.mutate({
        campaignMapId: activeCampaignMap.id,
        pinId: pinId ?? undefined,
        x,
        y,
      });

      set({
        partyLocation: {
          campaignMapId: activeCampaignMap.id,
          currentPinId: pinId ?? undefined,
          x,
          y,
          updatedAt: new Date(),
        },
      });
    } catch (error) {
      console.error('[CampaignMapStore] Set party location error:', error);
    }
  },

  // ==========================================================================
  // NAVIGATION
  // ==========================================================================

  travelToPin: async (pinId) => {
    const pin = get().pins.get(pinId);
    if (!pin) return null;

    try {
      // Update party location to the pin
      await get().setPartyLocation(pinId);

      // If the pin has a linked scene, return it for navigation
      if (pin.linkedSceneId) {
        // Switch to battle map mode
        set({ mapMode: 'battle' });
        return { sceneId: pin.linkedSceneId };
      }

      // If no linked scene, just stay on campaign map
      return null;
    } catch (error) {
      console.error('[CampaignMapStore] Travel error:', error);
      return null;
    }
  },

  // ==========================================================================
  // QUERIES
  // ==========================================================================

  getVisiblePins: (isGM) => {
    const { pins } = get();
    const allPins = Array.from(pins.values());

    if (isGM) {
      return allPins;
    }

    // Players only see non-hidden pins
    return allPins.filter((pin) => pin.state !== 'hidden');
  },

  getRevealedRegions: () => {
    const { regions } = get();
    return Array.from(regions.values()).filter((r) => r.isRevealed);
  },

  getPinsByRegion: (regionId) => {
    const { pins } = get();
    return Array.from(pins.values()).filter((p) => p.regionId === regionId);
  },

  getPinById: (pinId) => {
    return get().pins.get(pinId);
  },

  getRegionById: (regionId) => {
    return get().regions.get(regionId);
  },

  // ==========================================================================
  // REALTIME — pin changes push to all clients (fog redraws automatically)
  // ==========================================================================

  subscribeToPinRealtime: (campaignMapId: string) => {
    get().unsubscribeFromPinRealtime();

    console.log('[CampaignMapStore] Subscribing to pin changes:', campaignMapId);

    const { cleanup } = createSubscription({
      channelName: `pins-${campaignMapId}`,
      table: 'machina_campaign_pins',
      filter: `campaign_map_id=eq.${campaignMapId}`,
      event: '*',
      onPayload: (payload) => {
        const eventType = payload.eventType;

        if (eventType === 'DELETE') {
          const oldRow = payload.old as { id?: string };
          if (oldRow.id) {
            set((state) => {
              const newPins = new Map(state.pins);
              newPins.delete(oldRow.id!);
              return { pins: newPins, pinVersion: state.pinVersion + 1 };
            });
          }
          return;
        }

        // INSERT or UPDATE — parse the pin from DB row
        const row = payload.new as Record<string, unknown>;
        const pin: CampaignPin = {
          id: row.id as string,
          campaignMapId: row.campaign_map_id as string,
          x: row.x as number,
          y: row.y as number,
          name: row.name as string,
          description: (row.description as string) ?? undefined,
          iconType: (row.icon_type as CampaignPin['iconType']) ?? 'poi',
          customIconUrl: (row.custom_icon_url as string) ?? undefined,
          state: (row.state as PinState) ?? 'hidden',
          linkedBattleMapId: (row.linked_battle_map_id as string) ?? undefined,
          linkedSceneId: (row.linked_scene_id as string) ?? undefined,
          regionId: (row.region_id as string) ?? undefined,
          tags: (row.tags as string[]) ?? [],
          notes: (row.notes as string) ?? undefined,
          createdAt: new Date(row.created_at as string),
          updatedAt: new Date(row.updated_at as string),
        };

        console.log('[CampaignMapStore] Realtime pin update:', pin.name, pin.state);

        set((state) => {
          const newPins = new Map(state.pins);
          newPins.set(pin.id, pin);
          return { pins: newPins, pinVersion: state.pinVersion + 1 };
        });
      },
    });

    set({ realtimeCleanup: cleanup });
  },

  unsubscribeFromPinRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },
}));
